import React, { useState, useEffect, useMemo, useRef } from 'react'
import classNames from 'classnames'
import { Form as RBForm } from 'react-bootstrap'
import Imgix from 'react-imgix'
import axios from 'axios'
import {
  useErrorService,
  useNotificationService,
  useResolutionService,
} from '@abroad/components'
import useDebounce from '../common/useDebounce'
import API from '../../utils/API'
import { useParams } from 'react-router-dom'
import ExperienceDeleteIcon from './ExperienceDeleteIcon'
import ExperienceButton from './ExperienceButton'
import ImageCropModal from '../common/ImageCropModal'
import ExperienceConfirmModal from './ExperienceConfirmModal'
import LearningAttachedDndRow from './LearningAttachedDndRow'

const VIDEO_ROW_DND = 'LEARNING_WATCH_ROW'

const emptyVideoDraft = () => ({
  title: '',
  url: '',
  description: '',
  timeInMinute: '',
  thumbnail: '',
})

const EMPTY_VIDEO_FIELD_ERRORS = {
  title: '',
  url: '',
  timeInMinute: '',
  thumbnail: '',
}

const YOUTUBE_URL_REGEX =
  /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/

const mediaId = (item) => item?._id || item?.id

const truncateLabel = (text) => {
  const t = String(text || '').trim()
  if (t.length > 72) return `${t.slice(0, 69)}\u2026`
  return t
}

const normalizeVideo = (item) => {
  if (!item) return item
  const id = mediaId(item)
  return id ? { ...item, _id: id, id } : item
}

const videoSubtitle = (video) => {
  const parts = []
  if (video?.timeInMinute) {
    parts.push(`${video.timeInMinute} min`)
  }
  if (video?.description) {
    parts.push(video.description)
  }
  return parts.join(' · ')
}

const QuestVideos = ({
  videos = [],
  disabled = false,
  addVideo,
  removeVideo,
  onVideoUpdate,
  onSaveStateChange,
  onRefetchResources,
  layout = 'legacy',
  placeholder = 'Search video title…',
}) => {
  const Error = useErrorService()
  const Notification = useNotificationService()
  const { isMDScreen } = useResolutionService()
  const { questId } = useParams()
  const [questVideos, setQuestVideos] = useState([...videos, {}])
  const [allVideos, setAllVideos] = useState([])
  const [searchData, setSearchData] = useState([])
  const [inputFocus, setInputFocus] = useState(false)
  const [currentIndex, setCurrentIndex] = useState()
  const [localSearch, setLocalSearch] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [draft, setDraft] = useState(emptyVideoDraft())
  const [fieldErrors, setFieldErrors] = useState(EMPTY_VIDEO_FIELD_ERRORS)
  const [isSaving, setIsSaving] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [isReorderBusy, setIsReorderBusy] = useState(false)
  const [showRemoveImageConfirm, setShowRemoveImageConfirm] = useState(false)
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState(null)
  const [isDraggingImage, setIsDraggingImage] = useState(false)
  const [showImageCropModal, setShowImageCropModal] = useState(false)
  const [selectedImageFile, setSelectedImageFile] = useState(null)
  const imageRef = useRef(null)
  const questVideosRef = useRef([])
  const isReorderingRef = useRef(false)
  const lastSavedOrderRef = useRef('')
  const debouncedSearch = useDebounce(localSearch, 400)

  const listDisabled = disabled || isReorderBusy

  const getOrderKey = (list) =>
    (list || [])
      .map((video) => mediaId(video))
      .filter(Boolean)
      .join(',')

  const setQuestVideosSynced = (updater) => {
    setQuestVideos((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      questVideosRef.current = next
      return next
    })
  }

  useEffect(() => {
    if (layout === 'picker') {
      if (isReorderingRef.current || isReorderBusy) return
      const next = videos.filter((video) => mediaId(video)).map(normalizeVideo)
      setQuestVideosSynced(next)
      lastSavedOrderRef.current = getOrderKey(next)
      return
    }
    setQuestVideos([...videos, {}])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videos, layout])

  useEffect(() => {
    const getAllVideos = async () => {
      try {
        const { data } = await API.AdminAPI.quests.getAllVideo()
        setAllVideos((data || []).map(normalizeVideo))
      } catch (e) {}
    }
    getAllVideos()
  }, [])

  useEffect(() => {
    if (layout === 'picker') {
      const attachedIds = new Set(questVideos.map((video) => mediaId(video)))
      const query = debouncedSearch?.trim()?.toLowerCase() || ''
      const filtered = allVideos.filter((video) => {
        const id = mediaId(video)
        if (attachedIds.has(id)) {
          return false
        }
        if (!query) {
          return true
        }
        return (
          video?.title?.toLowerCase()?.includes(query) ||
          video?.description?.toLowerCase()?.includes(query)
        )
      })
      setSearchData(filtered)
      return
    }

    if (localSearch !== undefined) {
      const s = allVideos.filter((video) =>
        video?.title?.toLowerCase()?.includes(debouncedSearch?.toLowerCase()),
      )
      setSearchData(s)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, allVideos, questVideos, layout])

  const handleChange = (i, event) => {
    let values = [...questVideos]
    values[i] = {
      ...values[i],
      title: event.target.value,
    }
    setCurrentIndex(i)
    setQuestVideos(values)
  }

  const addClick = async (video, type) => {
    const id = mediaId(video)
    if (typeof onSaveStateChange === 'function') {
      onSaveStateChange('saving')
    }
    try {
      await API.AdminAPI.quests.addVideoToQuest(questId, id, {
        attach: type === 'add',
      })
      if (type === 'add') {
        addVideo && addVideo(normalizeVideo(video))
      }
      if (typeof onSaveStateChange === 'function') {
        onSaveStateChange('saved')
      }
    } catch (e) {
      Error.showError(e)
      if (typeof onSaveStateChange === 'function') {
        onSaveStateChange('error')
      }
      throw e
    }
  }

  const removeClick = async (i) => {
    let values = [...questVideos]
    if (values.length !== 1 || layout === 'picker') {
      const removed = values[i]
      try {
        await addClick(removed, 'remove')
        removeVideo && removeVideo(mediaId(removed))
      } catch (e) {
        return
      }
      values.splice(i, 1)
      setQuestVideosSynced(values)
      lastSavedOrderRef.current = getOrderKey(values)
      if (editingId && editingId === mediaId(removed)) {
        closeForm()
      }
      if (onVideoUpdate) {
        onVideoUpdate(values)
      }
    }
  }

  const restoreVideosAfterReorderFailure = async (orderBefore) => {
    if (typeof onRefetchResources === 'function') {
      try {
        const data = await onRefetchResources()
        if (data?.videos) {
          const restored = data.videos
            .filter((video) => mediaId(video))
            .map(normalizeVideo)
          setQuestVideosSynced(restored)
          lastSavedOrderRef.current = getOrderKey(restored)
          return
        }
      } catch (e) {
        // Fall through to local restore.
      }
    }
    setQuestVideosSynced(orderBefore)
    lastSavedOrderRef.current = getOrderKey(orderBefore)
    if (onVideoUpdate) {
      onVideoUpdate(orderBefore)
    }
  }

  const handleReorderDrop = async ({ id, fromIndex, toIndex }) => {
    if (listDisabled) return
    const before = [...(questVideosRef.current || [])]
    const from = before.findIndex((video) => mediaId(video) === id)
    if (from < 0) return
    const to =
      typeof toIndex === 'number' && toIndex >= 0 && toIndex < before.length
        ? toIndex
        : fromIndex
    if (from === to) return

    const next = [...before]
    const [moved] = next.splice(from, 1)
    next.splice(to, 0, moved)
    const orderedIds = next.map((video) => mediaId(video)).filter(Boolean)

    isReorderingRef.current = true
    setQuestVideosSynced(next)
    setIsReorderBusy(true)
    if (typeof onSaveStateChange === 'function') {
      onSaveStateChange('saving')
    }

    try {
      await API.AdminAPI.quests.reorderQuestResources(
        questId,
        'videos',
        orderedIds,
      )
      lastSavedOrderRef.current = getOrderKey(next)
      if (onVideoUpdate) {
        onVideoUpdate(next)
      }
      if (typeof onSaveStateChange === 'function') {
        onSaveStateChange('saved')
      }
    } catch (e) {
      Error.showError(e)
      if (typeof onSaveStateChange === 'function') {
        onSaveStateChange('error')
      }
      await restoreVideosAfterReorderFailure(before)
    } finally {
      setIsReorderBusy(false)
      isReorderingRef.current = false
    }
  }

  const requestRemoveClick = (i) => {
    setPendingRemoveIndex(i)
  }

  const confirmRemoveClick = async () => {
    const i = pendingRemoveIndex
    setPendingRemoveIndex(null)
    if (typeof i === 'number') {
      await removeClick(i)
    }
  }

  const pendingRemoveLabel = truncateLabel(
    typeof pendingRemoveIndex === 'number'
      ? questVideos[pendingRemoveIndex]?.title
      : '',
  )
  const pendingRemoveMessage = pendingRemoveLabel
    ? `\u201C${pendingRemoveLabel}\u201D will be detached from this experience.`
    : 'It will be detached from this experience.'

  const attachFromLibrary = async (video) => {
    if (listDisabled) return
    const normalized = normalizeVideo(video)
    const next = [...questVideos, normalized]
    setQuestVideosSynced(next)
    setLocalSearch('')
    try {
      await addClick(normalized, 'add')
      lastSavedOrderRef.current = getOrderKey(next)
      if (onVideoUpdate) {
        onVideoUpdate(next)
      }
    } catch (e) {
      setQuestVideosSynced(questVideos)
    }
  }

  const closeForm = () => {
    setShowCreate(false)
    setEditingId(null)
    setDraft(emptyVideoDraft())
    setFieldErrors(EMPTY_VIDEO_FIELD_ERRORS)
    setSelectedImageFile(null)
    setShowImageCropModal(false)
  }

  const openCreateForm = () => {
    setEditingId(null)
    setDraft(emptyVideoDraft())
    setFieldErrors(EMPTY_VIDEO_FIELD_ERRORS)
    setShowCreate(true)
  }

  const openEditForm = (video) => {
    if (disabled) return
    setEditingId(mediaId(video))
    setDraft({
      title: video.title || '',
      url: video.url || '',
      description: video.description || '',
      timeInMinute: video.timeInMinute || '',
      thumbnail: video.thumbnail || '',
    })
    setFieldErrors(EMPTY_VIDEO_FIELD_ERRORS)
    setShowCreate(true)
  }

  const validateThumbnailFile = (file) => {
    const allowedFormats = ['JPG', 'JPEG', 'PNG']
    const fileExtension = file?.name?.split('.').pop()?.toUpperCase()
    if (!allowedFormats.includes(fileExtension)) {
      Notification.showNotification(
        'Filetype must be one of [JPG, JPEG, PNG]',
        'danger',
      )
      return false
    }
    return true
  }

  const uploadThumbnail = async (file) => {
    const contentType = file.name
      .substr(file.name.lastIndexOf('.') + 1)
      .toUpperCase()
    const { data } = await API.AdminAPI.quests.getVideoThumbnailSignedURL(
      contentType,
    )
    if (!data) {
      throw new Error('Image upload failed')
    }
    await axios.put(data.url.url, file, {
      headers: { 'Content-Type': data.url.contentType },
    })
    return data.fileName
  }

  const openCropModal = (file) => {
    if (!file || !validateThumbnailFile(file)) return
    setSelectedImageFile(file)
    setShowImageCropModal(true)
  }

  const handleCropConfirm = async (croppedFile) => {
    if (!croppedFile) return
    setShowImageCropModal(false)
    setIsUploading(true)
    try {
      const fileName = await uploadThumbnail(croppedFile)
      setDraft((prev) => ({ ...prev, thumbnail: fileName }))
      setFieldErrors((prev) =>
        prev.thumbnail ? { ...prev, thumbnail: '' } : prev,
      )
    } catch (e) {
      Error.showError(e)
    } finally {
      setIsUploading(false)
      setSelectedImageFile(null)
    }
  }

  const saveVideo = async () => {
    if (disabled || isSaving || isUploading) return

    const nextErrors = { ...EMPTY_VIDEO_FIELD_ERRORS }
    if (!draft.title.trim()) {
      nextErrors.title = 'Required'
    }
    const url = draft.url.trim()
    if (!url) {
      nextErrors.url = 'Required'
    } else if (!YOUTUBE_URL_REGEX.test(url)) {
      nextErrors.url = 'Invalid YouTube URL'
    }
    const duration =
      draft.timeInMinute === '' || draft.timeInMinute == null
        ? undefined
        : Number(draft.timeInMinute)
    if (
      duration !== undefined &&
      (!Number.isInteger(duration) || duration < 1)
    ) {
      nextErrors.timeInMinute = 'Must be greater than zero'
    }
    if (!draft.thumbnail) {
      nextErrors.thumbnail = 'Required'
    }

    setFieldErrors(nextErrors)
    if (
      nextErrors.title ||
      nextErrors.url ||
      nextErrors.timeInMinute ||
      nextErrors.thumbnail
    ) {
      return
    }

    setIsSaving(true)
    const payload = {
      title: draft.title.trim(),
      url: draft.url.trim(),
      description: draft.description.trim(),
      timeInMinute: duration,
      thumbnail: draft.thumbnail || undefined,
    }
    try {
      if (editingId) {
        const { data } = await API.AdminAPI.quests.updateVideo(
          editingId,
          payload,
        )
        const updated = normalizeVideo(data || { ...payload, _id: editingId })
        setAllVideos((prev) =>
          prev.map((video) =>
            mediaId(video) === editingId ? { ...video, ...updated } : video,
          ),
        )
        const next = questVideos.map((video) =>
          mediaId(video) === editingId ? { ...video, ...updated } : video,
        )
        setQuestVideosSynced(next)
        if (onVideoUpdate) {
          onVideoUpdate(next)
        }
        Notification.showNotification('Video updated.', 'success')
        closeForm()
      } else {
        const { data } = await API.AdminAPI.quests.createVideo(payload)
        const created = normalizeVideo(data)
        setAllVideos((prev) => [created, ...prev])
        await attachFromLibrary(created)
        Notification.showNotification('Video created and attached.', 'success')
        closeForm()
      }
    } catch (e) {
      Error.showError(e)
    } finally {
      setIsSaving(false)
    }
  }

  const attachedVideos = useMemo(
    () => questVideos.filter((video) => mediaId(video)),
    [questVideos],
  )

  const hasThumbnail = Boolean(draft.thumbnail)
  const thumbSrc = hasThumbnail
    ? `${process.env.REACT_APP_IMG_SOURCE}/videos/${draft.thumbnail}`
    : ''

  if (layout === 'picker') {
    const attachedLabel =
      attachedVideos.length === 1
        ? '1 attached'
        : `${attachedVideos.length} attached`

    return (
      <>
        <div className='experience-audio-picker'>
          <div className='experience-audio-picker-attached'>
            <div className='experience-ap-head'>
              <h4>On this experience</h4>
              <span className='experience-ap-count'>{attachedLabel}</span>
            </div>
            <div className='experience-ap-body'>
              {attachedVideos.length === 0 ? (
                <div className='experience-ap-empty'>
                  <p>Nothing attached yet</p>
                  <small>Add video from the library on the right.</small>
                </div>
              ) : (
                attachedVideos.map((video, index) => {
                  const id = mediaId(video)
                  return (
                    <LearningAttachedDndRow
                      key={id}
                      dndType={VIDEO_ROW_DND}
                      id={id}
                      index={index}
                      title={video.title}
                      subtitle={videoSubtitle(video)}
                      typeLabel='Video'
                      isEditing={editingId === id}
                      disabled={listDisabled}
                      onDragEnd={handleReorderDrop}
                      onOpen={() => openEditForm(video)}
                      onRequestRemove={() =>
                        requestRemoveClick(
                          questVideos.findIndex((v) => mediaId(v) === id),
                        )
                      }
                    />
                  )
                })
              )}
            </div>
          </div>
          <div className='experience-audio-picker-divider' aria-hidden='true' />
          <div className='experience-audio-picker-library'>
            <div className='experience-ap-head'>
              <h4>Library</h4>
            </div>
            <div className='experience-ap-body'>
              <div className='experience-lib-filter'>
                <input
                  type='text'
                  placeholder={placeholder}
                  value={localSearch}
                  onChange={(e) => setLocalSearch(e.target.value)}
                  autoComplete='off'
                />
              </div>
              {searchData.length === 0 ? (
                <div className='experience-lib-empty'>
                  {localSearch.trim()
                    ? 'No matching video found.'
                    : 'All matching video is already attached.'}
                </div>
              ) : (
                searchData.map((video) => {
                  const id = mediaId(video)
                  const subtitle = videoSubtitle(video)
                  return (
                    <div className='experience-lib-item' key={id}>
                      <span className='copy'>
                        <b>{video.title}</b>
                        {subtitle ? <small>{subtitle}</small> : null}
                      </span>
                      <button
                        type='button'
                        className='experience-lib-track-add'
                        disabled={listDisabled}
                        onClick={() => attachFromLibrary(video)}>
                        Add
                      </button>
                    </div>
                  )
                })
              )}
            </div>
          </div>
        </div>
        {showCreate ? (
          <div aria-busy={isUploading || isSaving || undefined}>
            <div className='experience-video-editor'>
              <div>
                <div className='experience-f'>
                  <label>Title</label>
                  <input
                    className={classNames('experience-f-control', {
                      'is-invalid': Boolean(fieldErrors.title),
                    })}
                    type='text'
                    placeholder='Video title'
                    value={draft.title}
                    disabled={isUploading || isSaving}
                    onChange={(e) => {
                      const value = e.target.value
                      setDraft((prev) => ({ ...prev, title: value }))
                      if (fieldErrors.title) {
                        setFieldErrors((prev) => ({ ...prev, title: '' }))
                      }
                    }}
                  />
                  {fieldErrors.title ? (
                    <p className='experience-f-error mt-1 mb-0' role='alert'>
                      {fieldErrors.title}
                    </p>
                  ) : null}
                </div>
                <div className='experience-f mt-12px'>
                  <label>YouTube URL</label>
                  <input
                    className={classNames('experience-f-control', {
                      'is-invalid': Boolean(fieldErrors.url),
                    })}
                    type='url'
                    placeholder='https://www.youtube.com/watch?v='
                    value={draft.url}
                    disabled={isUploading || isSaving}
                    onChange={(e) => {
                      const value = e.target.value
                      setDraft((prev) => ({ ...prev, url: value }))
                      if (fieldErrors.url) {
                        setFieldErrors((prev) => ({ ...prev, url: '' }))
                      }
                    }}
                  />
                  {fieldErrors.url ? (
                    <p className='experience-f-error mt-1 mb-0' role='alert'>
                      {fieldErrors.url}
                    </p>
                  ) : null}
                </div>
                <div className='experience-f mt-12px'>
                  <label>Description</label>
                  <textarea
                    className='experience-f-control'
                    rows={3}
                    placeholder='What this video is for'
                    value={draft.description}
                    disabled={isUploading || isSaving}
                    onChange={(e) => {
                      const value = e.target.value
                      setDraft((prev) => ({
                        ...prev,
                        description: value,
                      }))
                    }}
                  />
                </div>
                <div className='experience-f mt-12px'>
                  <label>Duration (minutes)</label>
                  <input
                    className={classNames('experience-f-control', {
                      'is-invalid': Boolean(fieldErrors.timeInMinute),
                    })}
                    type='number'
                    min='1'
                    placeholder='e.g. 8'
                    value={draft.timeInMinute}
                    disabled={isUploading || isSaving}
                    onChange={(e) => {
                      const value = e.target.value
                      setDraft((prev) => ({
                        ...prev,
                        timeInMinute: value,
                      }))
                      if (fieldErrors.timeInMinute) {
                        setFieldErrors((prev) => ({
                          ...prev,
                          timeInMinute: '',
                        }))
                      }
                    }}
                  />
                  {fieldErrors.timeInMinute ? (
                    <p className='experience-f-error mt-1 mb-0' role='alert'>
                      {fieldErrors.timeInMinute}
                    </p>
                  ) : null}
                </div>
              </div>
              <div className='experience-f'>
                <label>Thumbnail</label>
                <div className='experience-img-slot' data-ratio='2:1'>
                  <div
                    className={classNames('experience-img-frame', {
                      'is-empty': !hasThumbnail && !isUploading,
                      'is-dragging': isDraggingImage,
                      'is-invalid': Boolean(fieldErrors.thumbnail),
                    })}
                    onClick={() => {
                      if (!isUploading && imageRef.current) {
                        imageRef.current.click()
                      }
                    }}
                    onDragEnter={(e) => {
                      e.preventDefault()
                      setIsDraggingImage(true)
                    }}
                    onDragOver={(e) => e.preventDefault()}
                    onDragLeave={(e) => {
                      e.preventDefault()
                      setIsDraggingImage(false)
                    }}
                    onDrop={(e) => {
                      e.preventDefault()
                      setIsDraggingImage(false)
                      const file = e.dataTransfer?.files?.[0]
                      if (file) openCropModal(file)
                    }}
                    role='button'
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        if (!isUploading && imageRef.current) {
                          imageRef.current.click()
                        }
                      }
                    }}>
                    {isUploading ? (
                      <span
                        className='experience-img-hover'
                        style={{ opacity: 1 }}>
                        Uploading…
                      </span>
                    ) : (
                      <span className='experience-img-hover'>
                        {isDraggingImage
                          ? 'Drop image to upload'
                          : hasThumbnail
                          ? 'Click to replace'
                          : 'Upload image'}
                      </span>
                    )}
                    {hasThumbnail ? (
                      <>
                        <Imgix
                          src={thumbSrc}
                          imgixParams={{
                            fit: 'crop',
                            crop: 'center',
                            ar: isMDScreen ? '2:1' : '4:2',
                            auto: 'format,compress',
                          }}
                          width={800}
                          htmlAttributes={{
                            alt: 'video thumbnail',
                            style: {
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            },
                          }}
                          className='lazyload'
                        />
                        {!isUploading ? (
                          <button
                            type='button'
                            className='experience-img-remove'
                            aria-label='Remove image'
                            onClick={(e) => {
                              e.stopPropagation()
                              e.preventDefault()
                              setShowRemoveImageConfirm(true)
                            }}>
                            ×
                          </button>
                        ) : null}
                      </>
                    ) : !isUploading ? (
                      <>
                        <b>Upload image</b>
                        <small>JPG, JPEG or PNG · cropped to 2:1</small>
                      </>
                    ) : null}
                    <RBForm.Control
                      type='file'
                      accept='image/jpg, image/jpeg, image/png'
                      onChange={(e) => {
                        const target = e.target
                        const file = e.target.files?.[0]
                        if (file) {
                          openCropModal(file)
                        }
                        if (target) target.value = ''
                      }}
                      style={{ display: 'none' }}
                      ref={imageRef}
                    />
                  </div>
                </div>
                {fieldErrors.thumbnail ? (
                  <p className='experience-f-error mt-1 mb-0' role='alert'>
                    {fieldErrors.thumbnail}
                  </p>
                ) : null}
              </div>
            </div>
            <div className='experience-form-actions'>
              <ExperienceButton
                variant='primary'
                size='sm'
                isLoading={isSaving}
                disabled={disabled || isUploading}
                onClick={saveVideo}>
                Save video
              </ExperienceButton>
              <ExperienceButton
                size='sm'
                disabled={isSaving || isUploading}
                onClick={closeForm}>
                Cancel
              </ExperienceButton>
            </div>
            <ImageCropModal
              show={showImageCropModal}
              onHide={() => {
                setShowImageCropModal(false)
                setSelectedImageFile(null)
              }}
              file={selectedImageFile}
              onConfirm={handleCropConfirm}
              isUploading={isUploading}
              aspect={isMDScreen ? 2 / 1 : 4 / 2}
              title='Crop Image'
            />
            <ExperienceConfirmModal
              show={showRemoveImageConfirm}
              onHide={() => setShowRemoveImageConfirm(false)}
              onConfirm={() => {
                setShowRemoveImageConfirm(false)
                setDraft((prev) => ({ ...prev, thumbnail: '' }))
              }}
              title='Remove this image?'
              message='The image will be cleared. You can upload another.'
              confirmLabel='Delete'
            />
          </div>
        ) : (
          <ExperienceButton
            size='sm'
            disabled={listDisabled}
            onClick={openCreateForm}>
            Add new video
          </ExperienceButton>
        )}
        <ExperienceConfirmModal
          show={pendingRemoveIndex != null}
          onHide={() => setPendingRemoveIndex(null)}
          onConfirm={confirmRemoveClick}
          title='Remove this resource?'
          message={pendingRemoveMessage}
          confirmLabel='Delete'
        />
      </>
    )
  }

  return (
    <div className='experience-f'>
      {questVideos.map((el, i) => (
        <div key={i} className='mb-2 position-relative'>
          <RBForm.Control
            type='text'
            placeholder={placeholder}
            onChange={(e) => {
              handleChange(i, e)
              setLocalSearch(e.target.value)
            }}
            onFocus={(e) => {
              setInputFocus(true)
              setCurrentIndex(i)
              setLocalSearch(e.target.value)
            }}
            onBlur={() => {
              setTimeout(() => {
                setInputFocus(false)
                setCurrentIndex(null)
                setLocalSearch('')
              }, 200)
            }}
            value={el?.title || ''}
            id='search-media'
            autoComplete='off'
            className={classNames(
              'd-inline experience-f-control black-color-placeholder',
              {
                'w-11/12': !disabled,
              },
            )}
            disabled={el?._id || disabled}
          />
          {searchData?.length > 0 &&
          inputFocus &&
          localSearch !== '' &&
          currentIndex === i ? (
            <ul className='search-media-list p-0'>
              {searchData?.map((video) => (
                <React.Fragment key={mediaId(video)}>
                  <li
                    onClick={async () => {
                      let values = [...questVideos]
                      values[i] = {
                        ...values[i],
                        ...normalizeVideo(video),
                      }
                      setQuestVideos([...values, {}])
                      setCurrentIndex(null)
                      setLocalSearch('')
                      await addClick(video, 'add')
                    }}
                    className='user-input'>
                    <strong>{video?.title}</strong> / {video?.description}
                  </li>
                  <hr className='m-0' />
                </React.Fragment>
              ))}
            </ul>
          ) : null}
          {!disabled && el?._id && (
            <div className='w-1/12 text-center d-inline-block'>
              <button
                type='button'
                className='experience-x'
                aria-label='Remove video'
                onClick={() => requestRemoveClick(i)}
                disabled={disabled}>
                <ExperienceDeleteIcon />
              </button>
            </div>
          )}
        </div>
      ))}
      <ExperienceConfirmModal
        show={pendingRemoveIndex != null}
        onHide={() => setPendingRemoveIndex(null)}
        onConfirm={confirmRemoveClick}
        title='Remove this resource?'
        message={pendingRemoveMessage}
        confirmLabel='Delete'
      />
    </div>
  )
}

export default QuestVideos
